// Type D — map_locate: a region name is given; the student finds and clicks it.
// The map shows all regions (no single highlighted entity); the student's job
// is to locate the named region by memory.

import { Check, X as XIcon, ArrowRight } from 'lucide-react';
import Button from '../../ui/Button';
import { cn } from '../../../lib/cn';

export default function MapLocatePanel({
  question,
  entityLabels,          // { id → human label }
  state,                 // { selectedEntityId, submitted, isCorrect }
  onSubmit,
  onNext,
  isLast,
}) {
  const { selectedEntityId, submitted, isCorrect } = state;
  const selectedLabel = selectedEntityId ? entityLabels[selectedEntityId] : null;
  const correctLabel = entityLabels[question.correct_entity_id];

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-5 overflow-y-auto">
        <p className="text-base leading-relaxed text-gray-900 dark:text-gray-100">
          {question.prompt}
        </p>

        <div
          className={cn(
            'rounded-lg border px-4 py-3 text-sm',
            selectedEntityId
              ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-200'
              : 'border-dashed border-gray-300 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400',
          )}
        >
          {selectedEntityId ? (
            <>
              <span className="font-medium">Selected:</span>{' '}
              <span>{selectedLabel}</span>
            </>
          ) : (
            <span>Click the correct region on the map.</span>
          )}
        </div>

        {submitted && (
          <div className="space-y-3">
            <div
              className={cn(
                'flex items-start gap-2 rounded-lg px-4 py-3 text-sm',
                isCorrect
                  ? 'bg-green-50 text-green-900 dark:bg-green-950/40 dark:text-green-200'
                  : 'bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-200',
              )}
            >
              {isCorrect ? (
                <>
                  <Check className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-semibold">Correct.</span> That is {correctLabel}.
                  </span>
                </>
              ) : (
                <>
                  <XIcon className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-semibold">Not quite.</span> The correct answer is{' '}
                    {correctLabel}.
                  </span>
                </>
              )}
            </div>

            <div className="rounded-lg bg-gray-50 dark:bg-gray-800/70 px-4 py-3 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
              {question.explanation}
            </div>
          </div>
        )}
      </div>

      <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
        {submitted ? (
          <Button onClick={onNext} variant="primary" className="w-full">
            {isLast ? 'See results' : 'Next question'}
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            variant="primary"
            className="w-full"
            disabled={!selectedEntityId}
          >
            Submit
          </Button>
        )}
      </div>
    </div>
  );
}
